import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState, useMemo } from 'react'
import {
  Building2, Activity, Eye, Users, ClipboardList, FileBarChart2,
  ChevronRight, AlertCircle, Clock, Briefcase, UserCheck,
  Layers, Target, LayoutTemplate, BarChart2,
  CheckCircle2, Circle, ChevronDown, X, Rocket,
} from 'lucide-react'
import { ProtectedRoute } from '../components/ProtectedRoute'
import { ErrorBoundary } from '../components/ErrorBoundary'
import { Box9QuadrantChart } from '../components/charts'
import type { QuadrantCount } from '../components/charts'
import { useAuth } from '../hooks/useAuth'
import { can } from '../lib/roles'
import { DEFAULT_COMPANY_ID } from '../lib/config'
import { getDashboardAnalytics, getRecentActivity, getOnboardingStatus, type DashboardAnalytics, type OnboardingStatusResult } from '../lib/analytics'
import { getBox9Matrix } from '../lib/box9grid'
import { listMetricDefinitions, listMetricSnapshots } from '../lib/metrics'
import { listMySessions } from '../lib/assessmentSessions'
import { MetricTrendChart } from '../components/charts'
import type { MetricDataPoint } from '../components/charts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'

// ─── Onboarding checklist ────────────────────────────────────────────────────

const DISMISSED_KEY  = 'pelajari_onboarding_dismissed';
const COLLAPSED_KEY  = 'pelajari_onboarding_collapsed';

type ChecklistStep = {
  key: keyof OnboardingStatusResult['status'];
  label: string;
  description: string;
  to: string;
  cta: string;
};

const CHECKLIST_STEPS: ChecklistStep[] = [
  {
    key: 'hasDivisions',
    label: 'Create divisions',
    description: 'Set up your org structure — departments, teams, or business units.',
    to: '/org/',
    cta: 'Go to Org',
  },
  {
    key: 'hasEmployees',
    label: 'Invite employees',
    description: 'Add your team members so they can access the platform.',
    to: '/org/',
    cta: 'Manage People',
  },
  {
    key: 'hasCompetencyCategories',
    label: 'Add competency categories',
    description: 'Group your competencies by domain (e.g. Engineering, Leadership).',
    to: '/kinerja/competencies',
    cta: 'Go to Competencies',
  },
  {
    key: 'hasAssessmentTemplates',
    label: 'Create an assessment template',
    description: 'Build the questionnaires used to evaluate employee competencies.',
    to: '/kinerja/templates',
    cta: 'Go to Templates',
  },
  {
    key: 'hasMilestoneTemplates',
    label: 'Add milestone templates',
    description: 'Define 30/90/180-day onboarding milestones for new hires by role.',
    to: '/kinerja/milestone-templates',
    cta: 'Go to Milestones',
  },
  {
    key: 'hasJobPostings',
    label: 'Post your first job',
    description: 'Start tracking candidates with AI-powered competency matching.',
    to: '/rekrut/jobs/',
    cta: 'Go to Recruitment',
  },
];

function OnboardingChecklist({ data }: { data: OnboardingStatusResult }) {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === 'true',
  );
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISSED_KEY) === 'true',
  );

  function toggleCollapse() {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(COLLAPSED_KEY, String(next));
  }

  function dismiss() {
    setDismissed(true);
    localStorage.setItem(DISMISSED_KEY, 'true');
  }

  // Hide completely once all done or dismissed
  if (dismissed || data.allComplete) return null;

  const { completedCount, totalCount, status } = data;
  const pct = Math.round((completedCount / totalCount) * 100);

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardContent className="pt-4 pb-4">
        {/* Header row */}
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10 shrink-0">
            <Rocket className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-foreground">Set up your organization</p>
              <Badge variant="secondary" className="text-[10px] font-semibold px-1.5 py-0">
                {completedCount}/{totalCount}
              </Badge>
            </div>
            {/* Progress bar */}
            <div className="mt-1.5 h-1.5 w-full bg-muted rounded-full overflow-hidden">
              <div
                className="h-full bg-primary rounded-full transition-all duration-500"
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={toggleCollapse}
              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground"
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <ChevronDown className={`w-4 h-4 transition-transform ${collapsed ? '' : 'rotate-180'}`} />
            </button>
            <button
              onClick={dismiss}
              className="p-1 rounded hover:bg-muted transition-colors text-muted-foreground"
              title="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Steps list */}
        {!collapsed && (
          <div className="mt-4 space-y-1">
            {CHECKLIST_STEPS.map((step) => {
              const done = status[step.key];
              return (
                <div
                  key={step.key}
                  className={`flex items-start gap-3 px-3 py-2.5 rounded-lg transition-colors ${
                    done ? 'opacity-50' : 'hover:bg-primary/5'
                  }`}
                >
                  {done ? (
                    <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                  ) : (
                    <Circle className="w-4 h-4 text-muted-foreground/40 shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium leading-snug ${done ? 'line-through text-muted-foreground' : 'text-foreground'}`}>
                      {step.label}
                    </p>
                    {!done && (
                      <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                    )}
                  </div>
                  {!done && (
                    <Link
                      to={step.to as any}
                      className="shrink-0 text-xs font-medium text-primary hover:underline flex items-center gap-0.5 mt-0.5"
                    >
                      {step.cta}
                      <ChevronRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export const Route = createFileRoute('/dashboard')({
  component: DashboardPage,
})

function DashboardPage() {
  return (
    <ProtectedRoute minimumRole="employee">
      <ErrorBoundary>
        <DashboardContent />
      </ErrorBoundary>
    </ProtectedRoute>
  )
}

function KpiCard({
  label, value, sub, icon: Icon, accent = false,
}: {
  label: string
  value: string | number
  sub?: string
  icon: React.ElementType
  accent?: boolean
}) {
  return (
    <Card>
      <CardContent className="pt-5">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{label}</p>
            <p className={`text-3xl font-semibold mt-1 tracking-tight ${accent ? 'text-primary' : 'text-foreground'}`}>
              {value}
            </p>
            {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
          </div>
          <div className={`p-2 rounded-md ${accent ? 'bg-brand-50' : 'bg-muted'}`}>
            <Icon className={`w-4 h-4 ${accent ? 'text-primary' : 'text-muted-foreground'}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

const ACTIVITY_ICONS: Record<string, React.ElementType> = {
  assessment: ClipboardList,
  metric: BarChart2,
  job: Briefcase,
  candidate: Users,
  division: Building2,
  hire: UserCheck,
  default: Activity,
}

const ACTIVITY_COLORS: Record<string, string> = {
  assessment: 'text-brand-500 bg-brand-50',
  metric: 'text-navy-600 bg-navy-50',
  job: 'text-amber-600 bg-amber-50',
  candidate: 'text-purple-600 bg-purple-50',
  default: 'text-muted-foreground bg-muted',
}

function DashboardContent() {
  const { user } = useAuth()
  // Derive stable scalars — avoids re-running the effect when the object reference
  // changes but the underlying values haven't.
  const userId = user?.id
  const companyId = user?.companyId ?? DEFAULT_COMPANY_ID

  const [stats, setStats] = useState<DashboardAnalytics | null>(null)
  const [activity, setActivity] = useState<Array<{ id: string; type: string; title: string; createdAt: string }>>([])
  const [box9Data, setBox9Data] = useState<QuadrantCount[]>([])
  const [trendData, setTrendData] = useState<MetricDataPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [pendingSessions, setPendingSessions] = useState(0)
  const [onboarding, setOnboarding] = useState<OnboardingStatusResult | null>(null)

  useEffect(() => {
    // AbortController lets us cancel in-flight fetches if the component unmounts
    // or the effect re-runs before the previous one finishes.
    const controller = new AbortController()
    let cancelled = false

    async function load() {
      setLoading(true)
      // Read token inside the effect so it is always fresh but never a dep.
      try {
        const [analyticsRes, activityRes, matrixRes, defsRes, sessionsRes, onboardingRes] = await Promise.all([
          getDashboardAnalytics(companyId),
          getRecentActivity(companyId),
          getBox9Matrix(companyId),
          listMetricDefinitions(companyId),
          userId ? listMySessions(userId, companyId) : Promise.resolve(null),
          can(user?.role, 'manage:org') ? getOnboardingStatus(companyId) : Promise.resolve(null),
        ])

        if (sessionsRes && sessionsRes.success && sessionsRes.data) {
          const pending = sessionsRes.data.sessions.filter(
            (s) => s.status === 'pending' || s.status === 'in_progress',
          ).length
          setPendingSessions(pending)
        }

        if (cancelled) return

        if (analyticsRes.success && analyticsRes.data) setStats(analyticsRes.data.dashboard)
        if (activityRes.success && activityRes.data) setActivity(activityRes.data.activity)
        if (onboardingRes?.success && onboardingRes.data) setOnboarding(onboardingRes.data)

        if (matrixRes.success && matrixRes.data) {
          const m = matrixRes.data.matrix
          setBox9Data([
            { quadrant: 'star', count: m.star },
            { quadrant: 'high-performer', count: m['high-performer'] },
            { quadrant: 'solid', count: m.solid },
            { quadrant: 'future-star', count: m['future-star'] },
            { quadrant: 'high-potential', count: m['high-potential'] },
            { quadrant: 'average', count: m.average },
            { quadrant: 'question-mark', count: m['question-mark'] },
            { quadrant: 'underperformer', count: m.underperformer },
            { quadrant: 'low-performer', count: m['low-performer'] },
          ])
        }

        if (defsRes.success && defsRes.data && defsRes.data.definitions.length > 0 && userId) {
          const firstDef = defsRes.data.definitions[0]
          const snapsRes = await listMetricSnapshots(companyId, userId)
          if (cancelled) return
          if (snapsRes.success && snapsRes.data) {
            const relevant = snapsRes.data.snapshots
              .filter((s) => s.metricDefinitionId === firstDef.id)
              .slice(0, 8)
              .reverse()
            setTrendData(
              relevant.map((s) => ({
                label: new Date(s.capturedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                value: typeof s.value === 'number' ? s.value : 0,
              }))
            )
          }
        }
      } catch (err) {
        if (cancelled) return
        setError('Failed to load dashboard data')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    load()

    return () => {
      cancelled = true
      controller.abort()
    }
  // Re-fetch only when the user's identity or company changes, not on every render.
  }, [companyId, userId])

  const greeting = () => {
    const h = new Date().getHours()
    if (h < 12) return 'Good morning'
    if (h < 17) return 'Good afternoon'
    return 'Good evening'
  }

  const userRole = user?.role
  const quickActions = useMemo(() => [
    { label: 'View Organization', to: '/org/', icon: Building2, show: can(userRole, 'view:org') },
    { label: 'My Assessments', to: '/kinerja/my-assessments', icon: ClipboardList, show: true },
    { label: 'Kinerja Metrics', to: '/kinerja/metrics', icon: BarChart2, show: can(userRole, 'view:kinerja') },
    { label: '9-Box Grid', to: '/wawasan/9box', icon: Eye, show: can(userRole, 'view:wawasan') },
    { label: 'AI Reports', to: '/reports', icon: FileBarChart2, show: can(userRole, 'view:wawasan'), search: { employee: undefined, period: undefined } },
    { label: 'Recruitment', to: '/rekrut', icon: Users, show: can(userRole, 'view:rekrut') },
  ].filter((a) => a.show), [userRole])

  return (
    <div className="space-y-6">
      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-semibold text-foreground tracking-tight">
          {greeting()}, {user?.name?.split(' ')[0] ?? 'there'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Here's what's happening at your company today.
        </p>
      </div>

      {pendingSessions > 0 && (
        <Link
          to="/kinerja/my-assessments/"
          className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800 hover:bg-amber-100 transition-colors"
        >
          <Clock className="w-4 h-4 shrink-0 text-amber-600" />
          <span className="flex-1">
            You have <strong>{pendingSessions}</strong> pending assessment{pendingSessions !== 1 ? 's' : ''} waiting for your response.
          </span>
          <ChevronRight className="w-4 h-4 shrink-0 text-amber-500" />
        </Link>
      )}

      {error && (
        <div className="flex items-center gap-2 p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-sm text-destructive">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {error}
        </div>
      )}

      {/* Onboarding checklist — only for admin/hr, auto-hides when complete */}
      {onboarding && <OnboardingChecklist data={onboarding} />}

      {/* KPI row */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <Card key={i}><CardContent className="pt-5"><div className="space-y-2">
              <div className="h-3 w-20 bg-muted rounded animate-pulse" />
              <div className="h-8 w-12 bg-muted rounded animate-pulse" />
            </div></CardContent></Card>
          ))}
        </div>
      ) : stats ? (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <KpiCard label="Total Employees" value={stats.totalUsers} icon={Users} />
          <KpiCard label="Open Jobs" value={stats.openJobs} icon={Briefcase} />
          <KpiCard label="Applications" value={stats.totalApplications} icon={ClipboardList} />
          <KpiCard label="Avg Match Score" value={`${Math.round(stats.averageMatchScore * 100)}%`} icon={Target} accent />
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="space-y-0.5">
              {quickActions.map((a) => {
                const Icon = a.icon
                return (
                  <Link
                    key={a.to + a.label}
                    to={a.to as any}
                    search={'search' in a ? (a.search as any) : undefined}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-md hover:bg-accent group transition-colors"
                  >
                    <div className="p-1.5 rounded bg-muted group-hover:bg-primary/10 transition-colors">
                      <Icon className="w-3.5 h-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <span className="text-sm text-foreground/80 group-hover:text-foreground flex-1">{a.label}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors" />
                  </Link>
                )
              })}
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-foreground">Recent Activity</CardTitle>
          </CardHeader>
          <CardContent className="pt-0">
            {loading ? (
              <div className="space-y-3">
                {[1,2,3].map(i => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded bg-muted animate-pulse shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-3/4 bg-muted rounded animate-pulse" />
                      <div className="h-2.5 w-1/3 bg-muted rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : activity.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-6">No recent activity.</p>
            ) : (
              <div className="space-y-0">
                {activity.map((item, idx) => {
                  const Icon = ACTIVITY_ICONS[item.type] ?? ACTIVITY_ICONS.default
                  const colors = ACTIVITY_COLORS[item.type] ?? ACTIVITY_COLORS.default
                  return (
                    <div key={item.id}>
                      <div className="flex items-start gap-3 py-2.5">
                        <div className={`p-1.5 rounded shrink-0 ${colors}`}>
                          <Icon className="w-3.5 h-3.5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-foreground leading-snug">{item.title}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Clock className="w-3 h-3 text-muted-foreground/60" />
                            <p className="text-xs text-muted-foreground">
                              {new Date(item.createdAt).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                        <Badge variant="muted" className="text-[10px] shrink-0 capitalize">{item.type.replace('_', ' ')}</Badge>
                      </div>
                      {idx < activity.length - 1 && <Separator />}
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts row */}
      {can(user?.role, 'view:wawasan') && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-foreground">9-Box Distribution</CardTitle>
                <Link to="/wawasan/9box/" className="text-xs text-primary hover:underline flex items-center gap-0.5">
                  Full grid <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="h-40 bg-muted rounded-lg animate-pulse" />
              ) : box9Data.length > 0 ? (
                <Box9QuadrantChart data={box9Data} height={228} />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No 9-box data yet.</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-semibold text-foreground">My Metric Trend</CardTitle>
                <Link to="/kinerja/metrics/" className="text-xs text-primary hover:underline flex items-center gap-0.5">
                  All metrics <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {loading ? (
                <div className="h-40 bg-muted rounded-lg animate-pulse" />
              ) : trendData.length > 0 ? (
                <MetricTrendChart data={trendData} />
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">No metric data recorded yet.</p>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* HR/Admin KPIs */}
      {stats && can(user?.role, 'manage:org') && (
        <div>
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Organization Overview</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <KpiCard label="Divisions" value={stats.totalDivisions} icon={Layers} />
            <KpiCard label="Competencies" value={stats.totalCompetencies} icon={Target} />
            <KpiCard label="Templates" value={stats.totalAssessmentTemplates} icon={LayoutTemplate} />
            <KpiCard label="Metric Defs" value={stats.totalMetricDefinitions} icon={BarChart2} />
          </div>
        </div>
      )}
    </div>
  )
}
